import { Box, Flex } from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toolbar } from './Toolbar';
import { StatusBar } from './StatusBar';
import { useViewStore } from '../../state/commandAuxStores';
import { DocStripHostContext } from '../../workspaces/docStripHost';

import { ViewCubeOptionsDialog } from '../viewport';
import { HelpDialog, HelpCenterModal } from '../help';
import { ManageDialogsHost } from '../manage';
import { CollabDialogsHost } from '../collaborate/CollabDialogsHost';
import { CollabRoomDialog } from '../collaboration';
import { PluginManagerDialog } from '../plugins';

interface LayoutProps {
    children: ReactNode;
}

/**
 * Main Layout Component - Yap
 * 
 * Provides the application shell with:
 * - Top Toolbar (Tools, view controls)
 * - Bottom Status Bar (Coordinates, tool info)
 * - Center Content Area (mode surface)
 *
 * Side panels are NOT rendered here anymore: every workbench
 * (BIM / 2D / 3D / Sketch) mounts the shared panel shell (@suite/panels)
 * and its dock strips render the workbench's own registered panels.
 */
import { SuiteTopbar } from "../SuiteTopbar";

export function Layout({ children }: LayoutProps) {
    // RIBBON/MENUBAR parity flag from the command engine: when off, the top
    // tool strip is hidden (the store keeps the state, so MENUBAR toggles
    // back). Previously these runner events had no listener anywhere.
    const ribbonVisible = useViewStore((s) => s.ribbonVisible);
    const location = useLocation();
    const navigate = useNavigate();
    const isHome = location.pathname === '/';
    const isBimMode = location.pathname === '/bim';
    // Sketch workbench brings its own toolbar (Sketch layout).
    const isSketchMode = location.pathname === '/sketch';
    // The document tab strip's chrome slot: the shared strip portals here
    // (WorkspaceShell stripPortalTarget), so it renders in the app chrome
    // — directly under the suite top bar — identically in every view mode.
    const [docStripHost, setDocStripHost] = useState<HTMLDivElement | null>(null);

    // WORKSPACE command: route navigation listener (view.workspace runner).
    useEffect(() => {
        const onSetWorkspace = (e: Event) => {
            const target = (e as CustomEvent<{ workspace?: string }>).detail?.workspace;
            if (!target) return;
            const route = ['bim', '2d', '3d', 'sketch'].includes(target) ? target : null;
            if (route) navigate(`/${route}`);
        };
        window.addEventListener('yap:set-workspace', onSetWorkspace);
        return () => window.removeEventListener('yap:set-workspace', onSetWorkspace);
    }, [navigate]);

    if (isHome) {
        return (
            <DocStripHostContext.Provider value={null}>
                <Box h="100%" w="100%" overflow="hidden" data-suite-selectable>
                    {children}
                </Box>
            </DocStripHostContext.Provider>
        );
    }

    return (
        <DocStripHostContext.Provider value={docStripHost}>
            <Flex h="100%" w="100%" direction="column" bg="surface.300">
                {/* The suite's served top bar — the app toolbar below stays. */}
                <SuiteTopbar />
                {/* Document tab strip host — the shared multi-document strip
                    portals in here, so it stays visible in every view mode
                    (collapses to zero height while it hosts nothing). */}
                <Box
                    ref={setDocStripHost}
                    className="yap-doc-strip-host"
                    data-yap-doc-strip-host
                />
            {/* Top Toolbar (RIBBON/MENUBAR toggle hides it via view store).
                BIM mode drops the shared tool strip entirely — its Collaborate
                and Plugins triggers live on the suite topbar in every mode.
                Sketch mode brings its own SketchToolbar, so the shared strip
                is dropped there too. */}
            {ribbonVisible && !isBimMode && !isSketchMode && <Toolbar />}

            {/* Main Content Area — the mounted workbench renders its own
                side panels through the shared panel shell's dock strips */}
            <Flex flex="1" overflow="hidden">
                {/* Center - Mode surface */}
                <Box
                    flex="1"
                    position="relative"
                    bg="bg.canvas"
                    overflow="hidden"
                    data-tour-id="viewport"
                >
                    {children}
                </Box>
            </Flex>

            {/* Bottom Status Bar */}
            <StatusBar />

            {/* Global Dialogs */}
            <ViewCubeOptionsDialog />
            <HelpDialog />
            <HelpCenterModal />
            <ManageDialogsHost />
            <CollabDialogsHost />
            <CollabRoomDialog />
            <PluginManagerDialog />
            </Flex>
        </DocStripHostContext.Provider>
    );
}
